import React from "react";
import * as api from "./drilldownApi";
import { flattenStretches } from "./WorkLog";

/**
 * useDrilldownData — the one read behind a person or project drill-down.
 *
 * Lifted out of DrilldownDrawer so the routed drill-down PAGES and the drawer
 * (still mounted by the Brain canvas) share a single data layer. The fetch
 * shape is deliberately unchanged from the drawer's: same endpoints, same
 * per-request failure isolation, so the numbers on a page and in the drawer
 * can never drift apart.
 *
 * Failure isolation is the point of the separate states:
 *   - the dossier failing kills the view (there is nothing to show)
 *   - `read` failing must not: metrics without a narrative is still useful
 *   - `worklog` failing must not: the narrative and the meters are different
 *     questions, and an empty log must never look like a broken one
 *
 * `range` MUST be referentially stable across renders — pass the memoised value
 * from useClassicRange() or useDashboardData(). An inline object literal here
 * re-fires every effect on every render.
 */

const isAbort = (e) => e?.name === "CanceledError" || e?.name === "AbortError";

export function useDrilldownData({ kind, id, name, range, withProjects = false }) {
  const isProject = kind === "project";
  const key = id ? `${kind}:${id}` : null;

  const [dossier, setDossier] = React.useState(null);
  const [read, setRead] = React.useState(null);
  const [readErr, setReadErr] = React.useState(null);
  const [related, setRelated] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const [worklog, setWorklog] = React.useState(null);
  const [worklogLoading, setWorklogLoading] = React.useState(true);
  const [worklogErr, setWorklogErr] = React.useState(null);

  /* ── the spine: dossier (person) or roster (project), plus the read ────── */
  React.useEffect(() => {
    if (!id) return undefined;
    const ac = new AbortController();
    let alive = true;

    setLoading(true); setError(null); setReadErr(null);
    setDossier(null); setRead(null); setRelated(null);

    (async () => {
      try {
        if (isProject) {
          const data = await api.projectMembers(id, range, { signal: ac.signal });
          if (!alive) return;
          setRelated(data);
          // No project narrator exists server-side yet, so the project read and
          // takeaways are derived in code from these same figures. Deterministic,
          // free, and every sentence quotes a number that is on screen.
          setRead({ sentences: api.projectRead(data, name), deterministic: true });
        } else {
          const d = await api.personDossier(id, range, { signal: ac.signal });
          if (!alive) return;
          setDossier(d);
          // The read is a separate model call — a failure there must not take
          // the whole page down, so it is caught on its own.
          api.personRead(id, range, { signal: ac.signal })
            .then((r) => { if (alive) setRead(r); })
            .catch((e) => { if (alive) setReadErr(e.message || "The read could not be generated."); });
        }
      } catch (e) {
        if (alive && !isAbort(e)) setError(e.message || "Could not load this drill-down.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; ac.abort(); };
  }, [key, range, isProject, id, name]);

  /* ── the written record — people only ─────────────────────────────────────
     A project's written narrative would be a different aggregation (many
     people, one project) and does not exist yet; rendering an empty one there
     would imply it should. */
  React.useEffect(() => {
    if (isProject || !id) return undefined;
    const ac = new AbortController();
    let alive = true;

    setWorklogLoading(true); setWorklogErr(null); setWorklog(null);
    api.personWorklog(id, range, { signal: ac.signal, refresh: true })
      .then((w) => { if (alive) setWorklog(w); })
      .catch((e) => { if (alive && !isAbort(e)) setWorklogErr(e.message || "The work log could not be loaded."); })
      .finally(() => { if (alive) setWorklogLoading(false); });

    return () => { alive = false; ac.abort(); };
  }, [isProject, id, range]);

  /* ── a person's project split — only where the view actually draws it ──── */
  React.useEffect(() => {
    if (!withProjects || isProject || !id) return undefined;
    const ac = new AbortController();
    let alive = true;
    api.personProjects(id, range, { signal: ac.signal })
      .then((r) => { if (alive) setRelated(r); })
      .catch(() => { /* the Projects view falls back to the dossier's own split */ });
    return () => { alive = false; ac.abort(); };
  }, [withProjects, isProject, id, range]);

  const stretches = React.useMemo(() => flattenStretches(worklog), [worklog]);

  return {
    isProject,
    dossier, read, readErr, related,
    worklog, worklogLoading, worklogErr, stretches,
    loading, error,
  };
}

export default useDrilldownData;
